/**
 * Presentation rules for the agent's interactive cards on phones (approvals, questions, artifacts, drafts),
 * kept out of the components so they can be tested and shared by the desktop and phone layouts.
 *
 * Everything here reads typed fields only (risk classes, effect flags, option counts), never the agent's prose.
 */
import type { ChangesetEffect } from './api'
import type { ArtifactRef } from './artifacts'
import { htmlToText } from './diff'
import type { UiQuestion } from './reducer'

/** Risk classes whose effects can't be taken back once they leave Lighthouse: sends, invites with guests, money. */
const IRREVERSIBLE_RISKS = new Set(['external_irreversible', 'financial'])
const EXTERNAL_RISKS = new Set(['external_reversible', 'external_irreversible', 'financial'])
/**
 * The desktop card's original rule for which outside effects to mention (calendar changes, shares, invoice
 * emails): kept so the list never shrinks. Effect summaries are written by the backend, not the agent.
 */
const MENTIONED_EFFECTS = /send|email|share|calendar|invoice/i

type Effect = Pick<ChangesetEffect, 'risk' | 'summary'> & Partial<Pick<ChangesetEffect, 'reversible' | 'kind'>>

/** Names in an effect's summary ("Archive thread 'Confirm your email' …"): email subjects and record names. */
const QUOTED = /'(?:[^']|'(?=\w))*'|‘(?:[^’]|’(?=\w))*’|“[^”]*”|"[^"]*"/gu

/**
 * Effects worth calling out as "Leaves Lighthouse" while a change waits for approval. Only the backend's own words
 * count: a thread whose subject says "invoice" or "email" isn't an email being sent.
 */
export function leavingEffects<T extends Effect>(effects: readonly T[] | null | undefined): T[] {
    return (effects ?? []).filter((e) => Boolean(e.summary) && e.risk !== 'read'
        && (IRREVERSIBLE_RISKS.has(e.risk) || MENTIONED_EFFECTS.test(e.summary.replace(QUOTED, ''))))
}

/** At most this many outside effects are spelled out; the rest are "+N more". */
export const LEAVING_SHOWN = 3

/**
 * Approving this needs a deliberate press and hold: something goes out that can't be called back (an email, an
 * invoice, an invite to other people). Known from the event's `max_risk` before the details load, and from the
 * recorded effects once they have.
 */
export function needsHoldToApprove(maxRisk: string | null | undefined, effects?: readonly Effect[] | null): boolean {
    if (maxRisk && IRREVERSIBLE_RISKS.has(maxRisk)) return true
    return (effects ?? []).some((e) => IRREVERSIBLE_RISKS.has(e.risk) || (e.reversible === false && EXTERNAL_RISKS.has(e.risk)))
}

/** At most this many options become quick-reply chips; more open the question sheet. */
export const QUICK_REPLY_MAX = 6

/**
 * How a question is answered on a phone:
 * - `chips`: one single-choice question with a few options: tap one and it's sent;
 * - `sheet`: several questions, multi-select or a long list: a summary card that opens the question sheet;
 * - `none`: nothing to pick (the dock composer answers it).
 */
export function questionMode(q: Pick<UiQuestion, 'options' | 'fields'>): 'chips' | 'sheet' | 'none' {
    if (q.fields.length) {
        const [only] = q.fields
        const single = q.fields.length === 1 && !only.multi && only.options.length > 0 && only.options.length <= QUICK_REPLY_MAX
        return single ? 'chips' : 'sheet'
    }
    if (!q.options.length) return 'none'
    return q.options.length <= QUICK_REPLY_MAX ? 'chips' : 'sheet'
}

/** "3 quick questions", "Pick any of 4", "Pick one of 9": the summary card's title. */
export function questionSummary(q: Pick<UiQuestion, 'options' | 'fields'>): string {
    if (q.fields.length > 1) return `${q.fields.length} quick questions`
    const f = q.fields[0]
    const n = f ? f.options.length : q.options.length
    if (f?.multi) return `Pick any of ${n}`
    return n ? `Pick one of ${n}` : '1 quick question'
}

/**
 * The records a turn touched, laid out for the phone transcript: single email drafts lead as their own mail
 * cards (they still need the creator), and the rest sit in one row, a swipeable carousel when there's more
 * than one.
 */
export function phoneArtifactLayout(list: readonly ArtifactRef[]): { lead: ArtifactRef[]; rest: ArtifactRef[]; carousel: boolean } {
    const lead = list.filter((a) => a.type === 'draft' && !a.items)
    const rest = list.filter((a) => !(a.type === 'draft' && !a.items))
    return { lead, rest, carousel: rest.length > 1 }
}

/** A plain-text preview of an email body: paragraphs joined, whitespace collapsed, cut on a word. */
export function draftPreview(body: string | null | undefined, max = 240): string {
    const text = htmlToText(body ?? '').replace(/\s+/g, ' ').trim()
    if (text.length <= max) return text
    return `${text.slice(0, max).replace(/\s+\S*$/, '')}…`
}

/** "Dana Lee" from `Dana Lee <dana@x.co>`, or the address; "+2" when there are more. */
export function recipientLine(to: unknown): string {
    const list = (Array.isArray(to) ? to : typeof to === 'string' ? to.split(',') : []).map((x) => String(x).trim()).filter(Boolean)
    if (!list.length) return ''
    const first = list[0]
    const name = first.includes('<') ? first.split('<')[0].trim().replace(/"/g, '') || first.replace(/.*<|>.*/g, '') : first
    return list.length > 1 ? `${name} +${list.length - 1}` : name
}

/** The confirmation line for an undo: what it puts back, shortened past four changes. */
export function undoMessage(lines: readonly string[]): string {
    if (!lines.length) return 'This puts everything back the way it was before this turn.'
    const shown = lines.slice(0, 4).map((l) => l.replace(/[.\s]+$/, ''))
    const more = lines.length - shown.length
    return `This reverses: ${shown.join(' · ')}${more > 0 ? ` · and ${more} more` : ''}.`
}

/** A review card swiped this far (of its width) commits on release. */
export const SWIPE_COMMIT_FRACTION = 0.32
/** Or flicked at least this fast (px/ms) the same way, after a small move. */
export const SWIPE_FLICK_VELOCITY = 0.55

/** What releasing a swiped review card does: right approves, left skips, otherwise it springs back. */
export function swipeOutcome(dx: number, velocity: number, width: number): 'approve' | 'skip' | null {
    const far = Math.abs(dx) >= width * SWIPE_COMMIT_FRACTION
    const flick = Math.abs(dx) > 24 && Math.abs(velocity) >= SWIPE_FLICK_VELOCITY && Math.sign(velocity) === Math.sign(dx)
    if (!far && !flick) return null
    return dx > 0 ? 'approve' : 'skip'
}
